import { useEffect, useMemo, useState } from "react";
import { Spinner, Alert } from "react-bootstrap";
import AddLocation from "./AddLocation";
import LocationTable from "./LocationTable";
import { locationApi } from "../../apiservices/locationApi";
import Pager from "../../components/Pager";
import useClientPaging from "../../hooks/useClientPaging";

export default function LocationPage() {
  const [locations, setLocations] = useState<any[]>([]);
  // Search by name, then page (the list is small, so both happen in the browser)
  const [search, setSearch] = useState("");
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...locations].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return q ? sorted.filter((l) => String(l.name).toLowerCase().includes(q)) : sorted;
  }, [locations, search]);
  const { pageItems, pagerProps } = useClientPaging(shown, 25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<any | null>(null);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await locationApi.getAll();
      setLocations(res.data ?? []);
    } catch (e: any) {
      setError(e.message ?? "Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleSave = async (payload: any) => {
    if (editingLocation) {
      await locationApi.update(editingLocation._id, payload);
    } else {
      await locationApi.create(payload);
    }
    await fetchLocations();
  };

  const handleEdit = (loc: any) => {
    setEditingLocation(loc);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await locationApi.remove(id);
      setLocations((prev) => prev.filter((l) => l._id !== id));
    } catch (e: any) {
      alert(e.message ?? "Delete failed");
    }
  };

  const openAddModal = () => {
    setEditingLocation(null);
    setShowModal(true);
  };

  return (
    <div className="container-fluid p-1">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 className="fw-bold mb-0">Location Management</h5>
          <small className="text-muted">Manage geo-fenced attendance zones</small>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <input
            type="search"
            className="form-control form-control-sm"
            style={{ width: 220 }}
            placeholder="Search location name"
            aria-label="Search location name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="btn btn-primary btn-sm px-3" onClick={openAddModal}>
            <i className="bi bi-plus-lg me-1" />
            Add Location
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table card */}
      <div className="card shadow-sm border-0">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            <LocationTable
              data={pageItems}
              startIndex={(pagerProps.page - 1) * pagerProps.limit}
              emptyText={search.trim() ? `No locations match “${search.trim()}”.` : undefined}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
            <Pager {...pagerProps} />
          </>
        )}
      </div>

      {/* Add / Edit modal */}
      <AddLocation
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSave}
        editData={editingLocation}
      />
    </div>
  );
}
